import { Collapse, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { FC, useMemo } from "react";
import { CheckoutOrder } from "tm-shared/types";

import FlexContainer from "@/components/FlexContainer";
import { PrimaryText } from "@/components/Typography/Styled";

import {
    StyledSACollapse,
    StyledSACollapseWrapper,
    StyledSAColumnNameText,
    StyledSATableWrapper,
    StyledSATotalSumText,
    StyledSAValueText,
} from "./Styled";

const { Panel } = Collapse;

interface StatisticAccordionProps {
    checkouts: CheckoutOrder[];
}

export const StatisticAccordion: FC<StatisticAccordionProps> = ({
    checkouts,
}) => {
    const data = useMemo(() => {
        const formMap: Record<
            string,
            {
                key: string;
                name: string;
                open: number;
                closed: number;
                total: number;
            }
        > = {};

        const accumulate = (order: CheckoutOrder) => {
            const form = order.paymentForm || "Невідома";
            const isClosed = order.closed;
            const total = Object.values(order.products).reduce(
                (sum, p) => sum + p.count * p.price,
                0,
            );

            if (!formMap[form]) {
                formMap[form] = {
                    key: form,
                    name: `Форма ${form}`,
                    open: 0,
                    closed: 0,
                    total: 0,
                };
            }

            if (isClosed) {
                formMap[form].closed += total;
            } else {
                formMap[form].open += total;
            }

            formMap[form].total = formMap[form].open + formMap[form].closed;
        };

        checkouts.forEach(accumulate);

        const values = Object.values(formMap);
        const totals = values.reduce(
            (acc, item) => {
                acc.open += item.open;
                acc.closed += item.closed;
                acc.total += item.total;
                return acc;
            },
            { key: "total", name: "Всього", open: 0, closed: 0, total: 0 },
        );

        return [...values, totals];
    }, [checkouts]);

    const columns: ColumnsType<(typeof data)[0]> = [
        {
            title: "",
            dataIndex: "name",
            key: "name",
            render: (text) => (
                <StyledSAColumnNameText>{text}</StyledSAColumnNameText>
            ),
        },
        {
            title: <StyledSAColumnNameText>Відкриті</StyledSAColumnNameText>,
            dataIndex: "open",
            key: "open",
            align: "right",
            render: (value: number) => (
                <StyledSAValueText>
                    {value.toLocaleString("uk-UA", {
                        minimumFractionDigits: 2,
                    })}{" "}
                    ₴
                </StyledSAValueText>
            ),
        },
        {
            title: <StyledSAColumnNameText>Закриті</StyledSAColumnNameText>,
            dataIndex: "closed",
            key: "closed",
            align: "right",
            render: (value: number) => (
                <StyledSAValueText>
                    {value.toLocaleString("uk-UA", {
                        minimumFractionDigits: 2,
                    })}{" "}
                    ₴
                </StyledSAValueText>
            ),
        },
        {
            title: <StyledSAColumnNameText>Разом</StyledSAColumnNameText>,
            dataIndex: "total",
            key: "total",
            align: "right",
            render: (value: number) => (
                <StyledSAValueText>
                    {value.toLocaleString("uk-UA", {
                        minimumFractionDigits: 2,
                    })}{" "}
                    ₴
                </StyledSAValueText>
            ),
        },
    ];

    const totalSum = data.find((item) => item.key === "total")?.total || 0;

    return (
        <StyledSACollapseWrapper>
            <StyledSACollapse>
                <Panel
                    key="1"
                    header={
                        <FlexContainer
                            justifyContent="space-between"
                            width="100%"
                        >
                            <PrimaryText
                                fontSize={14}
                                fontWeight="400"
                                lineHeight="140%"
                            >
                                Загальна сума
                            </PrimaryText>
                            <StyledSATotalSumText>
                                {totalSum.toLocaleString("uk-UA", {
                                    minimumFractionDigits: 2,
                                })}{" "}
                                грн.
                            </StyledSATotalSumText>
                        </FlexContainer>
                    }
                >
                    <StyledSATableWrapper>
                        <Table
                            columns={columns}
                            dataSource={data}
                            pagination={false}
                            bordered
                            rowClassName={() => "custom-row"}
                        />
                    </StyledSATableWrapper>
                </Panel>
            </StyledSACollapse>
        </StyledSACollapseWrapper>
    );
};
